/**
 * See the NOTICE file distributed with this work for additional information
 * regarding copyright ownership.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import classNames from 'classnames';
import type { ReactNode, ButtonHTMLAttributes } from 'react';

import { useShowTooltip } from 'src/shared/hooks/useShowTooltip';

import Tooltip from 'src/shared/components/tooltip/Tooltip';

import QuestionIcon from 'static/icons/icon_question_circle.svg';

import defaultStyles from './QuestionButton.module.css';

// Extra styling options based on where the button is located
type QuestionButtonStyleOption = 'inline' | 'in-input-field';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  helpText: ReactNode;
  styleOption?: QuestionButtonStyleOption;
};

const QuestionButton = (props: Props) => {
  const {
    helpText,
    styleOption = 'inline',
    className: classNameFromProps,
    ...otherProps
  } = props;
  const { elementRef, onClick, onTooltipCloseSignal, shouldShowTooltip } =
    useShowTooltip<HTMLButtonElement>();

  if (!helpText) {
    return null;
  }

  const className = classNames(
    defaultStyles.questionButton,
    {
      [defaultStyles[styleOption]]: styleOption
    },
    classNameFromProps
  );

  return (
    <button
      ref={elementRef}
      type="button"
      className={className}
      onClick={onClick}
      {...otherProps}
    >
      <QuestionIcon />
      {shouldShowTooltip && (
        <Tooltip
          anchor={elementRef.current}
          autoAdjust={true}
          onClose={onTooltipCloseSignal}
          delay={0}
        >
          {helpText}
        </Tooltip>
      )}
    </button>
  );
};

export default QuestionButton;
